import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useTheme } from "styled-components";
import type { PlaybackTransport } from "@carbon6/c6-frontend-design";
import type { MotionSeries, MotionWindow } from "./motionSeries";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { motionAxis, sampleMotionPoints, type MotionAxis } from "./motionRendering";
import styles from "./MotionSeriesView.module.css";

/**
 * 이 차트가 그릴 계열 하나. **무엇을 그릴지는 호출부가 정한다.**
 *
 * 차트가 색인에서 계열을 직접 고르면 색·파선 규칙이 차트 안에 들어간다. 그러면 같은 규칙을
 * 쓰는 다른 배치(채널 카드·그리퍼 행)가 각각 규칙을 다시 정해야 한다.
 */
export type MotionPlotLine = {
  series: MotionSeries;
  /** CSS 변수 문자열. 캔버스는 이 값을 `getComputedStyle` 로 계산해 쓴다. */
  color: string;
  /** 빈 배열이면 실선. */
  dash: readonly number[];
  /** 최댓값 배지가 부를 채널 이름. */
  title: string;
  /** 개요의 성긴 표본. 상세가 오면 바뀐다. 선을 옅게 그려 아직 정밀하지 않음을 표시한다. */
  preview?: boolean;
};

/** 호출부가 고른 계열에서 표시 표본만 뽑아 native Canvas로 그린다. 재생선은 CSS로 독립 갱신한다. */
export function MotionPlot({
  label,
  lines,
  window,
  compact = false,
  transport,
  onSelect,
  titlePrefix,
  axis: sharedAxis,
  fullRange,
  onFullRangeChange,
  headerAction,
}: {
  label: string;
  lines: readonly MotionPlotLine[];
  window: MotionWindow;
  /** 낮은 칸(예: 그리퍼 원본처럼 한 줄 옆에 두는 자리). 캔버스 높이만 줄인다. */
  compact?: boolean;
  transport: PlaybackTransport | null;
  onSelect: (seconds: number) => void;
  /** 접근명 앞에 붙일 이름. 같은 지표 차트가 채널마다 있으면 접근명이 모두 같아진다. */
  titlePrefix?: string;
  /** 좌우 열이 같은 y축을 쓰도록 상위가 계산해 전달한 y축. 없으면 이 차트가 y축을 계산한다. */
  axis?: MotionAxis;
  /** 상위가 y축 범위 토글 상태를 가질 때 전달한다. 없으면 이 차트가 자기 상태를 쓴다. */
  fullRange?: boolean;
  onFullRangeChange?: (next: boolean) => void;
  /** 제목 줄 오른쪽 자리에 대신 표시한다. 전달하면 단위 도움말(`?`)을 그리지 않는다. */
  headerAction?: ReactNode;
}) {
  const name = titlePrefix === undefined ? label : `${titlePrefix} ${label}`;
  const [ownFullRange, setOwnFullRange] = useState(false);
  const showFullRange = fullRange ?? ownFullRange;
  const setFullRange = onFullRangeChange ?? setOwnFullRange;
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(640);
  const theme = useTheme();
  useEffect(() => {
    if (!element) return;
    const resize = () => setWidth(Math.max(1, element.clientWidth || 640));
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  const { drawn, axis } = useMemo(() => {
    const drawn = lines.map((line) => ({
      color: line.color,
      dash: line.dash,
      preview: line.preview === true,
      unit: line.series.unit,
      points: sampleMotionPoints(
        line.series.points,
        { start: window.start, end: window.end },
        width,
      ),
    }));
    // y축은 줄인 표본이 아니라 원본으로 계산한다. y축이 픽셀 폭과 무관해야 좌우 열이 같은 y축을 쓴다.
    const series = lines.map((line) => line.series);
    return {
      drawn,
      axis:
        sharedAxis ?? motionAxis(series, { start: window.start, end: window.end }, showFullRange),
    };
  }, [lines, window.start, window.end, width, sharedAxis, showFullRange]);
  const { min, max, actualMax, clipped, outlying, count, maximum } = axis;
  useEffect(() => {
    const node = canvas.current;
    if (!node) return;
    const context = node.getContext("2d");
    if (!context) return;
    const height = compact ? 64 : 112;
    const ratio = globalThis.devicePixelRatio || 1;
    node.width = Math.round(width * ratio);
    node.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    const color = (value: string) => {
      node.style.color = value;
      return getComputedStyle(node).color;
    };
    context.strokeStyle = color("var(--c6-color-border-subtle)");
    context.lineWidth = 0.5;
    context.beginPath();
    for (const y of [0, height / 2, height]) {
      context.moveTo(0, y);
      context.lineTo(width, y);
    }
    context.stroke();
    const span = Math.max(window.end - window.start, 1e-9);
    for (const line of drawn) {
      context.globalAlpha = line.preview ? 0.55 : 1;
      context.strokeStyle = color(line.color);
      context.setLineDash([...line.dash]);
      context.lineWidth = 1.4;
      context.fillStyle = context.strokeStyle;
      context.beginPath();
      let connected = false;
      const outside: number[] = [];
      for (let i = 0; i < line.points.length; i++) {
        const point = line.points[i]!;
        if (point.v === null) {
          connected = false;
          continue;
        }
        const x = ((point.t - window.start) / span) * width;
        // y축 범위 밖 값은 위 끝에 그린다. 범위 밖에 그리면 이웃 차트 위에 겹친다.
        const y = height - ((Math.min(point.v, max) - min) / Math.max(max - min, 1e-9)) * height;
        if (connected) context.lineTo(x, y);
        else {
          context.moveTo(x, y);
          // 개요의 결측 사이에 홀로 남은 극값도 점으로 표시한다. moveTo만 하면 사라진다.
          if (line.points[i + 1]?.v == null) context.fillRect(x - 1, y - 1, 2, 2);
        }
        if (point.v > max) outside.push(x);
        connected = true;
      }
      context.stroke();
      // y축 범위 밖 값은 위 끝에 눈금을 그려 발생 시각을 표시한다. 값은 최댓값 배지가 표시한다.
      for (const x of outside) context.fillRect(x - 1, 0, 2, 3);
    }
    context.globalAlpha = 1;
  }, [drawn, min, max, width, compact, window.start, window.end, theme]);
  const drag = useRef<{
    pointerId: number;
    left: number;
    width: number;
    start: number;
    end: number;
  } | null>(null);
  const pointerClick = useRef(false);
  const lastSeek = useRef<number | null>(null);
  const seek = (time: number) => {
    lastSeek.current = Math.max(window.start, Math.min(window.end, time));
    onSelect(lastSeek.current);
  };
  const dragTo = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    seek(
      active.start +
        Math.max(0, Math.min(1, (event.clientX - active.left) / active.width)) *
          (active.end - active.start),
    );
  };
  const endDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    event.currentTarget.removeAttribute("data-dragging");
    if (event.currentTarget.hasPointerCapture?.(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const span = Math.max(window.end - window.start, 1e-9);
  // y축 최댓값은 채널 키로 온다. 차트는 구조 색인을 모르므로, 표시 이름은 계열 목록에서 찾는다.
  const maximumLabel = maximum
    ? `${name} 최댓값 ${valueLabel(maximum.value)} · ${
        lines.find((line) => line.series.channel === maximum.channel)?.title ?? maximum.channel
      } · ${maximum.t.toFixed(6)}초로 이동`
    : undefined;
  // y축을 좁혔을 때는 접근명에 이 수가 y축 눈금이 아니라고 적는다.
  const peakLabel = maximumLabel === undefined ? undefined : `${maximumLabel} · 축 범위 밖`;
  return (
    <div className={styles.plot} data-compact={compact || undefined}>
      <div className={styles.plotHeading}>
        <span>{label}</span>
        <span>
          {[...new Set(lines.flatMap((l) => (l.series.unit ? [l.series.unit] : [])))].join(" · ")}
          {headerAction ?? (
            <button
              type="button"
              className={styles.help}
              aria-label={`${name} 원본 단위 설명`}
              title="속력·가속도 크기는 원본 위치 x/y/z의 시간 미분 벡터 크기입니다. 속력은 원본 위치 단위/초, 가속도는 원본 위치 단위/초²이며 위치 좌표의 최댓값과 단위가 다릅니다. 단위 정보가 없으면 m/s로 환산하지 않습니다."
            >
              ?
            </button>
          )}
        </span>
      </div>
      {/* 빈 자리의 뜻은 두 가지다. 계열을 끈 경우에 "표본이 없다"를 표시하면 사용자가 데이터를 의심한다.
          빈 자리도 차트와 같은 틀을 그린다. 그래서 계열을 켜고 끌 때 카드 높이가 바뀌지 않는다. */}
      {lines.length === 0 || !count ? (
        <div className={styles.canvas} data-empty="true">
          <div className={styles.canvasMessage}>
            <EmptyState size="sm" mode="plain">
              {lines.length === 0
                ? "표시할 계열을 선택하세요."
                : `이 시간대에 유효한 ${label} 표본이 없습니다.`}
            </EmptyState>
          </div>
        </div>
      ) : (
        <div className={styles.canvas} ref={setElement}>
          <button
            type="button"
            className={styles.seek}
            aria-label={`${name} 그래프에서 시각 선택`}
            onPointerDown={(event) => {
              if (event.button !== 0 || drag.current !== null) return;
              const rect = event.currentTarget.getBoundingClientRect();
              if (rect.width <= 0) return;
              event.preventDefault();
              event.currentTarget.focus();
              event.currentTarget.setPointerCapture?.(event.pointerId);
              event.currentTarget.dataset.dragging = "true";
              drag.current = {
                pointerId: event.pointerId,
                left: rect.left,
                width: rect.width,
                start: window.start,
                end: window.end,
              };
              pointerClick.current = true;
              dragTo(event);
            }}
            onPointerMove={dragTo}
            onPointerUp={(event) => {
              dragTo(event);
              endDrag(event);
            }}
            onPointerCancel={endDrag}
            onLostPointerCapture={endDrag}
            onClick={(event) => {
              if (pointerClick.current && event.detail !== 0) {
                pointerClick.current = false;
                return;
              }
              const rect = event.currentTarget.getBoundingClientRect();
              if (rect.width > 0)
                seek(
                  window.start +
                    Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) * span,
                );
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                event.preventDefault();
                seek(
                  (transport?.getSnapshot().currentTimeSeconds ??
                    lastSeek.current ??
                    window.start) + (event.key === "ArrowRight" ? 0.1 : -0.1),
                );
              }
            }}
          >
            <canvas
              ref={canvas}
              aria-hidden="true"
              data-channels={lines.map((l) => l.series.channel).join(",")}
            />
          </button>
          {maximum && !clipped ? (
            <button
              type="button"
              className={styles.yMax}
              title={maximumLabel}
              aria-label={maximumLabel}
              onClick={() => onSelect(maximum.t)}
            >
              {valueLabel(max)}
            </button>
          ) : (
            <span className={styles.yMax}>{valueLabel(max)}</span>
          )}
          {/* y축을 낮췄을 때만 따로 표시한다. y축 눈금(max)과 실제 최댓값은 다른 수다. 한 자리에
              겹쳐 두면 어느 쪽이 y축 눈금인지 알 수 없다. */}
          {clipped && maximum ? (
            <button
              type="button"
              className={styles.yPeak}
              title={peakLabel}
              aria-label={peakLabel}
              onClick={() => onSelect(maximum.t)}
            >
              ↑ {valueLabel(actualMax)}
            </button>
          ) : null}
          {outlying ? (
            <button
              type="button"
              className={styles.rangeToggle}
              aria-pressed={showFullRange}
              aria-label={`${name} ${showFullRange ? "주요 분포에 맞춘 축으로" : "전체 범위 축으로"} 바꾸기`}
              title={
                showFullRange
                  ? "주요 분포에 맞춰 축을 좁힙니다. 범위 밖 값은 위 끝 눈금과 최댓값 배지로 남습니다."
                  : "실제 최댓값까지 축을 폅니다."
              }
              onClick={() => setFullRange(!showFullRange)}
            >
              {showFullRange ? "주요 분포" : "전체 범위"}
            </button>
          ) : null}
          <span className={styles.yMin}>{valueLabel(min)}</span>
          <span className={styles.playhead} aria-hidden="true" />
        </div>
      )}
      <div className={styles.axis}>
        <span>{window.start.toFixed(2)}초</span>
        <span>{window.end.toFixed(2)}초</span>
      </div>
    </div>
  );
}
function valueLabel(value: number): string {
  return value === 0
    ? "0"
    : Math.abs(value) < 0.001
      ? value.toExponential(2)
      : Number(value.toPrecision(4)).toString();
}
